import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const WorkOrderCreation = ({ equipment, technicians, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    equipmentId: '',
    priority: 'medium',
    assignedTo: '',
    scheduledDate: '',
    estimatedCost: ''
  });

  const [attachments, setAttachments] = useState([]);

  const equipmentOptions = equipment?.map(eq => ({
    value: eq?.id,
    label: `${eq?.name} - ${eq?.location}`
  }));

  const technicianOptions = technicians?.map(tech => ({
    value: tech?.id,
    label: `${tech?.name} (${tech?.skills?.join(', ')})`
  }));

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    // In production, call equipmentLifecycleService.createWorkOrder
    console.log('Creating work order:', formData);
    onSuccess();
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e?.target?.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Create Work Order</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Work Order Title <span className="text-error">*</span>
            </label>
            <Input
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Detailed Description <span className="text-error">*</span>
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Provide detailed information about the problem"
              rows={4}
              required
              className="w-full px-3 py-2 rounded-md border border-input bg-white text-black focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Equipment Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Equipment <span className="text-error">*</span>
            </label>
            <Select
              options={equipmentOptions}
              value={formData?.equipmentId}
              onChange={(value) => setFormData({ ...formData, equipmentId: value })}
              placeholder="Select equipment"
              required
            />
          </div>

          {/* Priority */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Priority <span className="text-error">*</span>
            </label>
            <Select
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => setFormData({ ...formData, priority: value })}
              placeholder="Select priority"
              required
            />
          </div>

          {/* Technician Assignment */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Assign Technician
            </label>
            <Select
              options={technicianOptions}
              value={formData?.assignedTo}
              onChange={(value) => setFormData({ ...formData, assignedTo: value })}
              placeholder="Auto-assign or select technician"
            />
          </div>

          {/* Scheduled Date */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Scheduled Date
            </label>
            <Input
              type="date"
              value={formData?.scheduledDate}
              onChange={(e) => setFormData({ ...formData, scheduledDate: e?.target?.value })}
            />
          </div>

          {/* Estimated Cost */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Estimated Cost
            </label>
            <Input
              type="number"
              value={formData?.estimatedCost}
              onChange={(e) => setFormData({ ...formData, estimatedCost: e?.target?.value })}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Photo Attachments */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Photo Attachments
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload photos</p>
              </label>
            </div>
            {attachments?.length > 0 && (
              <div className="mt-3 space-y-2">
                {attachments?.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm text-foreground">{file?.name}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev?.filter((_, i) => i !== index))}
                      className="text-error hover:text-error/80"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              iconName="CheckCircle2"
              iconPosition="left"
            >
              Create Work Order
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkOrderCreation;